import React, { useMemo, useState } from "react";
import { Card, EmptyState } from "../../components/Ui";
import { formatDate } from "../../metrics";
import { formatTarget } from "./AgpUi";
import { todayEt } from "../../hooks/usePeriod";

/**
 * Lists manual actual entries for a goal document and, when permitted, lets
 * the user add dated entries for any manually tracked numeric driver.
 */
export default function ActualsPanel({ title, subtitle, drivers = [], entries = [], canEdit, onAdd, onDelete }) {
  const manualDrivers = useMemo(() => drivers.filter((d) => d.source === "manual" && d.format !== "text"), [drivers]);
  const [open, setOpen] = useState(entries.length > 0);
  const [form, setForm] = useState({ driverKey: manualDrivers[0] ? manualDrivers[0].key : "", date: todayEt().toISODate(), value: "", note: "" });
  const [saving, setSaving] = useState(false);

  const labelFor = (key) => (drivers.find((d) => d.key === key) || {}).label || key;
  const formatFor = (key) => (drivers.find((d) => d.key === key) || {}).format || "number";

  const submit = async (e) => {
    e.preventDefault();
    const value = Number(String(form.value).replace(/[$,%\s]/g, ""));
    if (!form.driverKey || !Number.isFinite(value)) return;
    setSaving(true);
    const res = await onAdd({ driverKey: form.driverKey, date: form.date, value, note: form.note });
    setSaving(false);
    if (res && res.ok) setForm((f) => ({ ...f, value: "", note: "" }));
  };

  if (!manualDrivers.length && !entries.length) return null;

  return (
    <Card
      title={title}
      subtitle={subtitle}
      actions={
        <button type="button" className="bd-btn bd-btn--ghost" onClick={() => setOpen((o) => !o)}>
          {open ? "Hide" : `Show (${entries.length})`}
        </button>
      }
    >
      {open && (
        <div className="bd-agp-actuals">
          {canEdit && manualDrivers.length > 0 && (
            <form className="bd-agp-actuals__form" onSubmit={submit}>
              <label>
                <span>Driver</span>
                <select className="bd-select" value={form.driverKey} onChange={(e) => setForm((f) => ({ ...f, driverKey: e.target.value }))}>
                  {manualDrivers.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Date</span>
                <input type="date" className="bd-input" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} required />
              </label>
              <label>
                <span>Amount</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="bd-input"
                  placeholder={formatFor(form.driverKey) === "currency" ? "e.g. 125000" : "e.g. 12"}
                  value={form.value}
                  onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                  required
                />
              </label>
              <label className="bd-agp-actuals__note">
                <span>Note</span>
                <input type="text" className="bd-input" placeholder="optional, e.g. Q2 fees" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
              </label>
              <button type="submit" className="bd-btn bd-btn--primary" disabled={saving || !form.value}>
                Add entry
              </button>
            </form>
          )}
          {!entries.length ? (
            <EmptyState title="No actuals recorded yet" message={canEdit ? "Add dated entries above; they are summed into the driver's actual." : undefined} />
          ) : (
            <div className="bd-table-wrap bd-table-wrap--dense">
              <table className="bd-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Driver</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th>Note</th>
                    <th>Entered by</th>
                    {canEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id}>
                      <td>{formatDate(e.date)}</td>
                      <td>{labelFor(e.driverKey)}</td>
                      <td style={{ textAlign: "right" }}>
                        <strong>{formatTarget(e.value, formatFor(e.driverKey))}</strong>
                      </td>
                      <td className="bd-muted">{e.note || "—"}</td>
                      <td className="bd-muted">{e.enteredBy || "—"}</td>
                      {canEdit && (
                        <td style={{ textAlign: "right" }}>
                          <button type="button" className="bd-btn bd-btn--ghost" onClick={() => onDelete(e.id)} title="Remove entry">
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
